import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { recurringApi } from '@/api/endpoints';
import { authApi } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export const Route = createFileRoute('/dashboard/sessoes/recorrencias/')({ component: RecorrenciasIndexPage });

function RecorrenciasIndexPage() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const isTherapist = !!me?.therapistProfile;

  const { data = [], isLoading } = useQuery({ queryKey: ['recurring'], queryFn: recurringApi.list });

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Séries recorrentes</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isTherapist ? 'Agendamentos periódicos com seus clientes.' : 'Suas séries com profissionais.'}
          </p>
        </div>
        {isTherapist && (
          <Link to="/dashboard/sessoes/recorrencia/nova">
            <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-5">+ Nova série</Button>
          </Link>
        )}
      </div>

      {isLoading ? (
        <p className="text-slate-500 text-sm">Carregando...</p>
      ) : data.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="font-bold text-slate-700">Nenhuma série criada</p>
            <p className="text-sm text-slate-500 mt-2">
              Use séries para criar pacotes semanais ou mensais com pagamento único ou por sessão.
            </p>
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-3">
          {data.map((r: any) => (
            <li key={r.id}>
              <RecurringRow series={r} perspective={isTherapist ? 'THERAPIST' : 'CLIENT'} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecurringRow({ series, perspective }: { series: any; perspective: 'THERAPIST' | 'CLIENT' }) {
  const peer = perspective === 'THERAPIST' ? series.client : series.therapist;
  const peerName: string = peer?.user?.name ?? '—';
  const peerImage: string | null = peer?.avatarUrl ?? peer?.user?.image ?? null;
  const initial = peerName.charAt(0).toUpperCase();

  const { doneCount, totalCount, nextSession } = useMemo(() => {
    const sessions = (series.sessions ?? []) as Array<{ status: string; scheduledAt: string }>;
    const total = sessions.length || series.totalSessions || 0;
    const done = sessions.filter((s) => s.status === 'DONE').length;
    const now = Date.now();
    const next = sessions.find(
      (s) =>
        s.status !== 'DONE' &&
        s.status !== 'CANCELLED' &&
        new Date(s.scheduledAt).getTime() >= now,
    );
    return { doneCount: done, totalCount: total, nextSession: next };
  }, [series]);

  const progressPct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  const freqLabel = series.frequency === 'WEEKLY' ? 'Semanal' : 'Mensal';
  const modalityLabel =
    series.modality === 'ONLINE' ? 'Online' :
    series.modality === 'PRESENCIAL' ? 'Presencial' : 'Online ou Presencial';
  const paymentLabel = series.paymentMode === 'UPFRONT_BATCH' ? 'Pré-pago' : 'Por sessão';

  const nextLabel = nextSession
    ? new Date(nextSession.scheduledAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
    : series.status === 'CANCELLED' ? 'Cancelada' : '—';

  return (
    <Link
      to="/dashboard/sessoes/recorrencias/$id"
      params={{ id: series.id }}
      className="block bg-white rounded-2xl shadow-sm hover:shadow-md transition border border-transparent hover:border-teal-200 p-5"
    >
      <div className="flex items-center gap-4">
        {peerImage ? (
          <img src={peerImage} alt="" className="size-12 rounded-full object-cover" />
        ) : (
          <div className="size-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-base">
            {initial}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-800 truncate">{peerName}</p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <Pill>{freqLabel}</Pill>
            <Pill>{modalityLabel}</Pill>
            <Pill>{paymentLabel}</Pill>
            <span className="text-xs text-slate-400 ml-1">{series.startTime}</span>
          </div>
        </div>

        <div className="text-right shrink-0 hidden sm:block">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Próxima</p>
          <p className="text-sm font-bold text-slate-700 mt-0.5">{nextLabel}</p>
        </div>

        <span className="text-slate-300 text-lg shrink-0">→</span>
      </div>

      {totalCount > 0 && (
        <div className="mt-3.5 flex items-center gap-3">
          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-teal-500 transition-all"
              style={{ width: `${progressPct}%` }}
              aria-label={`${doneCount} de ${totalCount} concluídas`}
            />
          </div>
          <span className="text-xs text-slate-400 font-medium tabular-nums whitespace-nowrap">
            {doneCount}/{totalCount}
          </span>
        </div>
      )}
    </Link>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium border border-slate-200">
      {children}
    </span>
  );
}
